/**
 * ConnectionStatus - Displays reconnection banner when connection is lost
 *
 * Shows a banner at the top of the screen when:
 * - Socket is reconnecting (shows attempt count)
 * - Connection failed permanently (shows error)
 * - Successfully reconnected (briefly shows success message)
 */

import React, { useEffect, useState } from 'react';
import { useSocket } from '../contexts/SocketContext';

const ConnectionStatus: React.FC = () => {
  const { socket, isConnected, isReconnecting, reconnectAttempts, maxReconnectAttempts, lastError } = useSocket();
  // A deploy: the server says it's restarting, or this page reconnects to a new one (games in progress
  // ended, since rooms live in the server's memory).
  const [update, setUpdate] = useState<'restarting' | 'updated' | null>(null);
  const bootId = React.useRef<string | null>(null);
  useEffect(() => {
    if (!socket) return;
    const hello = ({ bootId: id }: { bootId: string }) => {
      if (bootId.current && bootId.current !== id) setUpdate('updated');
      else setUpdate(u => (u === 'restarting' ? null : u));
      bootId.current = id;
    };
    const restarting = () => setUpdate('restarting');
    socket.on('site:hello', hello);
    socket.on('site:restarting', restarting);
    socket.emit('site:hello');
    return () => { socket.off('site:hello', hello); socket.off('site:restarting', restarting); };
  }, [socket]);
  const [showConnected, setShowConnected] = useState(false);
  const [wasDisconnected, setWasDisconnected] = useState(false);
  const [everConnected, setEverConnected] = useState(false);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);   // the first connect of a page isn't a reconnect

  // Track when we were disconnected so we can show "Connected" message briefly
  useEffect(() => {
    if (!isConnected) {
      if (everConnected) setWasDisconnected(true);
    } else if (!everConnected) {
      setEverConnected(true);
    } else if (wasDisconnected) {
      // Just reconnected - show success message briefly
      setShowConnected(true);
      const timer = setTimeout(() => {
        setShowConnected(false);
        setWasDisconnected(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isConnected, wasDisconnected, everConnected]);

  if (update) {
    return (
      <div role="status" className="fixed top-0 left-0 right-0 z-50 bg-sky-700 text-white py-2 px-4 text-center text-sm font-medium shadow-lg">
        {update === 'restarting'
          ? 'The site is updating and will be back in a minute. Games in progress will end; leagues are kept.'
          : 'The site was just updated, so any game that was in progress has ended. Leagues are kept.'}
        {update === 'updated' && <button type="button" onClick={() => setUpdate(null)} className="ml-3 underline">OK</button>}
      </div>
    );
  }

  // The browser knows at once when the network is gone; the socket only notices after a while.
  if (!online) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-orange-600 text-white py-2 px-4 text-center text-sm font-medium shadow-lg">
        You're offline. Moves are kept and sent when you're back.
      </div>
    );
  }

  // Show nothing if connected and not showing success message
  if (isConnected && !showConnected) {
    return null;
  }

  // Show success message briefly after reconnecting
  if (isConnected && showConnected) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-green-600 text-white py-2 px-4 text-center text-sm font-medium shadow-lg animate-fade-in">
        <span className="inline-flex items-center gap-2">
          <span className="text-green-200">&#10003;</span>
          Connected
        </span>
      </div>
    );
  }

  // Show permanent error (reconnection failed completely)
  if (lastError && !isReconnecting) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white py-3 px-4 text-center shadow-lg">
        <div className="flex items-center justify-center gap-3">
          <span className="text-red-200 text-xl">&#9888;</span>
          <span className="font-medium">{lastError}</span>
          <button
            onClick={() => window.location.reload()}
            className="ml-4 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Show reconnecting banner
  if (isReconnecting) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-600 text-white py-2 px-4 text-center text-sm font-medium shadow-lg">
        <div className="flex items-center justify-center gap-3">
          <span className="inline-block animate-spin text-yellow-200">&#8635;</span>
          <span>
            Reconnecting... ({reconnectAttempts}/{maxReconnectAttempts})
          </span>
        </div>
      </div>
    );
  }

  // Show disconnected state (before reconnection attempts start); a page still making its first
  // connection hasn't lost anything.
  if (!isConnected && everConnected) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-orange-600 text-white py-2 px-4 text-center text-sm font-medium shadow-lg">
        <div className="flex items-center justify-center gap-2">
          <span className="inline-block animate-pulse text-orange-200">&#9679;</span>
          <span>Connection lost - attempting to reconnect...</span>
        </div>
      </div>
    );
  }

  return null;
};

export default ConnectionStatus;
